import React, { useEffect, useState } from 'react'
import { endOfDay, startOfDay } from 'date-fns'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { theme } from '../../../../theme/theme'
import { DateTimeInput } from '../../../common/DateTimeInput'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { FacilityCalendarSection } from './FacilityCalendarSection'
import { FacilityCalendarTimeSlots } from '../FacilityCalendarTimeSlots'
import { ITimeSlot } from './interfaces'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { NBookings } from '../../../../interfaces/services/bookings.interfaces'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { FacilityCalendarUnitSelect } from '../FacilityCalendarUnitSelect'
import { useUserReachedMaxBookings } from './useUserReachedMaxBookings'
import { Alert } from '../../../common/Alert'
import { AlertType } from '../../../common/Alert/alert-type'
import { FacilityCalendarBookingCard } from '../FacilityCalendarBookingCard'
import { IFacilityWithFiles } from '../../../../interfaces/services/facilities.interfaces'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { unitQueries } from '../../../../api/units'
import { bookingQueries, createBooking } from '../../../../api/bookings'
import {
  bookingsForUserAndFacilityForSpecificDate,
  futureBookingsForUnits,
} from '../../../../state/stores/bookings'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { i18nSocietyType } from '../../../../i18n'

interface FacilityCalendarProps {
  facility: IFacilityWithFiles
  society: ISocietiesModel
}

export const FacilityCalendar = ({
  facility,
  society,
}: FacilityCalendarProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const userId = useAuthenticatedUserId() as string

  const { data: userUnits, isLoading: isLoadingUserUnits } = useQuery(
    unitQueries.getUserUnits(userId)
  )

  const { data: facilityBookings, isLoading: isLoadingBookings } = useQuery(
    bookingQueries.getBookingsPerFacility(facility._id)
  )

  const userUnitsForSociety = userUnits?.filter(
    (_unit) => _unit.societyId === society._id && !_unit.isUnassigned
  )

  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>(
    userUnitsForSociety && userUnitsForSociety.length > 0
      ? userUnitsForSociety[0]._id
      : undefined
  )

  const userBookings = futureBookingsForUnits(
    userUnitsForSociety?.map((_unit) => _unit._id) || [],
    facilityBookings || []
  )

  const userHasReachedMaxNumberOfBookings = useUserReachedMaxBookings(
    selectedUnitId || '',
    facility,
    facilityBookings || []
  )

  const [selectedDate, _setSelectedDate] = useState(new Date())
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<
    ITimeSlot | undefined
  >(undefined)

  const setSelectedDate = (date: Date): void => {
    _setSelectedDate(date)
    setSelectedTimeSlot(undefined)
  }

  const userBookingsForDate = bookingsForUserAndFacilityForSpecificDate(
    userBookings || [],
    facility._id,
    startOfDay(selectedDate),
    endOfDay(selectedDate)
  )

  // Set default unitId if not present on initial load
  useEffect(() => {
    if (
      !selectedUnitId &&
      userUnitsForSociety &&
      userUnitsForSociety.length > 0
    ) {
      setSelectedUnitId(userUnitsForSociety[0]._id)
    }
  }, [selectedUnitId, userUnitsForSociety])

  const facilityBookingDisabled = facility.booking === false
  const bookingEnabled =
    selectedTimeSlot && facility._id && userId && society._id && selectedUnitId

  const currentUserIsAdmin = isUserAdminInSociety(society, userId)

  const { mutate: mutateCreateBooking, isPending: isCreatingBooking } =
    useMutation({
      mutationFn: createBooking,
      onSuccess: () => {
        setToastNotification(
          ToastType.SUCCESS,
          translate('facilityView.calendar.flashMessage.createBookingSuccess')
        )
        setSelectedTimeSlot(undefined)
      },
      onError: () => {
        setToastNotification(
          ToastType.DANGER,
          translate('facilityView.calendar.flashMessage.createBookingFailure')
        )
      },
    })

  const onCreateBooking = async (bookAsAdmin?: boolean): Promise<void> => {
    if (!bookingEnabled) {
      setToastNotification(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
      return
    }

    const body: NBookings.NCreate.IRequestBody = {
      // Interface is invalid
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      startDate: selectedTimeSlot.startDate.getTime(),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      endDate: selectedTimeSlot.endDate.getTime(),
      facilityId: facility._id,
      societyId: facility.societyId,
      unitId: selectedUnitId,
      userId,
      isAdmin: bookAsAdmin,
    }
    mutateCreateBooking(body)
  }

  const fetchingBookings = isLoadingUserUnits || isLoadingBookings
  const creatingBooking = isCreatingBooking

  if (!selectedUnitId) {
    return (
      <Alert
        text={translate('facilityView.calendar.alert.noUnit', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        type={AlertType.DANGER}
      />
    )
  }

  if (facilityBookingDisabled) {
    return (
      <Alert
        text={translate('facilityView.calendar.alert.bookingDisabledText')}
        type={AlertType.DANGER}
      />
    )
  }

  const getBookingAvailability = ():
    | { startDate: string | null; endDate: string | null }
    | undefined => {
    const bookingAvailability = facility?.bookingAvailability
    if (bookingAvailability === undefined || bookingAvailability.length === 0) {
      return undefined
    }
    return bookingAvailability[0]
  }

  const bookingAvailability = getBookingAvailability()

  const getIsSelectedDateGreaterThanFacilityStartDate = (): boolean => {
    if (bookingAvailability?.startDate) {
      return selectedDate > new Date(bookingAvailability?.startDate)
    }
    return true
  }

  const isSelectedDateGreaterThanFacilityStartDate =
    getIsSelectedDateGreaterThanFacilityStartDate()

  return (
    <div className="flex w-full flex-col gap-10">
      {userUnitsForSociety && userUnitsForSociety.length > 1 && (
        <FacilityCalendarSection
          title={translate('facilityView.calendar.sections.chooseUnit.title', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
        >
          <FacilityCalendarUnitSelect
            selectedUnitId={selectedUnitId}
            setSelectedUnitId={setSelectedUnitId}
            userUnits={userUnitsForSociety}
          />
        </FacilityCalendarSection>
      )}
      <FacilityCalendarSection
        title={translate('facilityView.calendar.sections.chooseDate.title')}
        className="flex flex-col lg:w-1/6"
      >
        <DateTimeInput
          minDate={
            bookingAvailability?.startDate
              ? new Date(bookingAvailability?.startDate)
              : new Date()
          }
          maxDate={
            bookingAvailability?.endDate
              ? new Date(bookingAvailability?.endDate)
              : undefined
          }
          value={selectedDate}
          onChange={setSelectedDate}
          blockedDates={[
            ...(facility.blockedDates ?? []).map((blockedDate) =>
              dayjs(blockedDate).toDate()
            ),
            ...(facility.bookingGranularity === 'full-day'
              ? (facilityBookings ?? []).map((booking) =>
                  dayjs(booking.startDate).toDate()
                ) || []
              : []),
          ]}
        />
      </FacilityCalendarSection>
      {isSelectedDateGreaterThanFacilityStartDate && (
        <FacilityCalendarSection
          title={translate('facilityView.calendar.sections.chooseSlot.title')}
          loading={fetchingBookings}
        >
          <FacilityCalendarTimeSlots
            facility={facility}
            selectedDate={selectedDate}
            selectedTimeSlot={selectedTimeSlot}
            setSelectedTimeslot={setSelectedTimeSlot}
            userHasReachedMaxNumberOfBookings={
              userHasReachedMaxNumberOfBookings && !currentUserIsAdmin
            }
          />
        </FacilityCalendarSection>
      )}
      {facility.bookingMessage && (
        <FacilityCalendarSection
          title={translate(
            'facilityView.calendar.sections.bookingMessage.title'
          )}
          dividerTop
        >
          <p
            className="w-full whitespace-pre-line md:w-1/2"
            style={theme.textVariants.base}
          >
            {facility.bookingMessage}
          </p>
        </FacilityCalendarSection>
      )}
      <div className="flex flex-col gap-4">
        {userHasReachedMaxNumberOfBookings && !currentUserIsAdmin && (
          <Alert
            type={AlertType.DANGER}
            text={translate(
              'facilityView.calendar.alert.unitMaxNumberBookings'
            )}
          />
        )}
        <FacilityCalendarSection
          title={translate('facilityView.calendar.sections.yourBooking.title')}
          dividerTop
          hideHeader={!selectedTimeSlot && userBookingsForDate.length === 0}
        >
          <div className="flex flex-wrap gap-4">
            {userBookingsForDate.map((booking) => (
              <FacilityCalendarBookingCard
                key={booking._id}
                type="booking"
                booking={booking}
                facility={facility}
                startDate={new Date(booking.startDate)}
                endDate={new Date(booking.endDate)}
              />
            ))}
            {selectedTimeSlot && (
              <FacilityCalendarBookingCard
                type="timeSlot"
                facility={facility}
                createBooking={onCreateBooking}
                startDate={selectedTimeSlot.startDate}
                endDate={selectedTimeSlot.endDate}
                loading={creatingBooking}
              />
            )}
          </div>
        </FacilityCalendarSection>
      </div>
    </div>
  )
}
